// ---------------------------------------------------------------------------
// Monitoring schedule list component
// ---------------------------------------------------------------------------

import type { admin } from '@noxverify/api-contracts';
import { DataTable, type Column } from './DataTable';
import { StatusBadge } from './StatusBadge';
import { useFormatters } from '../hooks/useFormatters';

interface MonitoringScheduleListProps {
  schedules: admin.MonitoringScheduleResponse[];
  onPause?: (scheduleId: string) => void;
  onResume?: (scheduleId: string) => void;
}

const SCHEDULE_TYPE_LABELS: Record<string, string> = {
  screening: 'Screening',
  registry: 'Registry',
  adverse_media: 'Adverse Media',
  pep: 'PEP',
};

const FREQUENCY_LABELS: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const target = new Date(dateStr).getTime();
  const diff = target - now;

  if (diff < 0) return 'Overdue';

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (days > 0) return `in ${days}d`;
  if (hours > 0) return `in ${hours}h`;
  return 'Soon';
}

export function MonitoringScheduleList({
  schedules,
  onPause,
  onResume,
}: MonitoringScheduleListProps) {
  const f = useFormatters();
  const columns: Column<admin.MonitoringScheduleResponse>[] = [
    {
      key: 'entityId',
      header: 'Entity',
      render: (s) => (
        <div>
          <span className="text-sm font-mono text-primary">{s.entityId}</span>
          <span className="ms-2 text-xs text-ink-muted">{s.entityType}</span>
        </div>
      ),
    },
    {
      key: 'scheduleType',
      header: 'Type',
      render: (s) => (
        <span className="text-sm text-ink">
          {SCHEDULE_TYPE_LABELS[s.scheduleType] ?? s.scheduleType}
        </span>
      ),
    },
    {
      key: 'frequency',
      header: 'Frequency',
      render: (s) => (
        <span className="text-sm text-ink">
          {FREQUENCY_LABELS[s.frequency] ?? s.frequency}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (s) => <StatusBadge value={s.status} />,
    },
    {
      key: 'nextRunAt',
      header: 'Next Run',
      render: (s) => (
        <div className="text-sm">
          <span className="text-ink">{formatRelativeTime(s.nextRunAt)}</span>
          <span className="block text-xs text-ink-muted tabular-nums font-mono">
            {f.date(s.nextRunAt, { dateStyle: 'medium' })}
          </span>
        </div>
      ),
    },
    {
      key: 'lastRunAt',
      header: 'Last Run',
      render: (s) => (
        <span className="text-sm text-ink-muted">
          {s.lastRunAt ? f.date(s.lastRunAt, { dateStyle: 'medium' }) : '--'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (s) => (
        <div className="flex gap-1">
          {s.status === 'active' && onPause && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onPause(s.id); }}
              className="px-2 py-1 text-xs font-medium bg-warning-tint hover:bg-warning-tint text-warning rounded transition-colors"
            >
              Pause
            </button>
          )}
          {s.status === 'paused' && onResume && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onResume(s.id); }}
              className="px-2 py-1 text-xs font-medium bg-primary-tint hover:bg-primary-tint text-primary rounded transition-colors"
            >
              Resume
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={schedules}
      rowKey={(s) => s.id}
      emptyMessage="No monitoring schedules found"
      ariaLabel="Monitoring schedules list"
    />
  );
}
